import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MaterialReactTable } from "material-react-table";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ScienceIcon from "@mui/icons-material/Science";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { toast } from "react-toastify";

import { axiosInstance } from "../../../../../api/axios";
import { useProjectApi } from "../../../hooks/useProjectApi";
import { MRT_Localization_KO } from "../../../constants/localiztion";
import { COLORS, FONT_PROPS } from "../../../styles/colors";
import { AutoPilotGuide } from "../../../components/common/AutoPilotGuide";
import { EmptyState } from "../../../components/common/EmptyState";
import { BreadcrumbsTable } from "../../../components/common/HelperFunc";

const EMPTY_FORM = {
  product_name: "",
  manufacturer: "",
  substance_name: "",
  cas_no: "",
  content_ratio: "",
  hazard_class: "",
  dangerous_goods_act: "",
  main_usage: "",
  emergency_contact: "",
  revision: 1,
  revised_at: "",
  expire_at: "",
  machine_ids: [],
};

const FORM_DATA_SKIP_KEYS = new Set([
  "id",
  "is_active",
  "created_at",
  "updated_at",
  "machines",
  "machine_count",
]);

const REQUIRED_FIELDS = [
  { key: "product_name", label: "제품명" },
  { key: "substance_name", label: "주요 물질명" },
];

const PARENT_TITLE = "정보관리";
const CHILD_TITLE = "물질안전보건자료(MSDS) 관리";

const pageContainerSx = {
  width: "100%",
  height: "100%",
  flex: 1,
  minHeight: 0,
  p: { xs: "16px", md: "16px 32px 16px 32px" },
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  bgcolor: "var(--mui-palette-background-paper)",
};

const standardPanelSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "0.375rem",
  bgcolor: "#fff",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.05)",
};

const iconActionButtonSx = {
  width: 40,
  height: 40,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "0.375rem",
  bgcolor: "#fff",
  "&:hover": {
    bgcolor: "primary.lightOpacity",
    borderColor: "primary.main",
  },
};

function MsdsSummaryCard({ icon, label, value, color = "#2563eb", helper }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        height: "100%",
        boxShadow: "none",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "0.375rem",
            display: "grid",
            placeItems: "center",
            color,
            bgcolor: `${color}14`,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 700 }}
          >
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
            {value}
          </Typography>
          {helper ? (
            <Typography variant="caption" color="text.secondary">
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

const MSDS_AUTOPILOT_STEPS = [
  {
    target: '[data-autopilot="msds-header"]',
    title: "MSDS 관리 흐름",
    description:
      "제품명, 주요 물질, CAS No., 유해성 분류와 원본 PDF를 한 화면에서 관리합니다. 새 데이터 저장이나 삭제 없이 화면 사용법만 안내합니다.",
  },
  {
    target: '[data-autopilot="msds-actions"]',
    title: "등록",
    description: "MSDS 등록 버튼에서 신규 물질과 PDF 원본을 입력합니다.",
  },
  {
    target: '[data-autopilot="msds-summary"]',
    title: "만료 상태 요약",
    description:
      "전체 건수, PDF 등록 여부, 만료 임박과 만료 건수를 먼저 확인해 우선 관리할 문서를 빠르게 찾을 수 있습니다.",
  },
  {
    target: '[data-autopilot="msds-list"]',
    title: "MSDS 목록",
    description:
      "목록에서는 제품명, 물질명, 유해성, 개정 버전, 만료 상태를 확인합니다. 원본 PDF가 있으면 바로 열어볼 수 있습니다.",
  },
  {
    target: '[data-autopilot="msds-table-actions"]',
    title: "검색과 수정",
    description:
      "제품명, 물질명, CAS No.로 검색하고 수정 아이콘으로 개정 정보와 PDF를 갱신합니다.",
  },
];

const MSDS_AUTOPILOT_MACHINE_COPY = {
  '[data-autopilot="msds-header"]': {
    title: "MSDS 관리 흐름",
    description:
      "제품명, 주요 물질, CAS No., 유해성 분류, 원본 PDF와 사용 설비를 한 화면에서 관리합니다. 설비 연결은 MSDS별 적용 대상을 확인하기 위한 정보입니다.",
  },
  '[data-autopilot="msds-actions"]': {
    title: "MSDS 등록",
    description:
      "MSDS 등록 버튼을 누르면 문서 정보, PDF 원본, 사용 설비를 함께 입력합니다. 사용 설비는 여러 개 선택할 수 있고 수정 화면에서 추가하거나 제거할 수 있습니다.",
  },
  '[data-autopilot="msds-summary"]': {
    title: "만료 상태 요약",
    description:
      "전체 건수, PDF 등록 여부, 만료 임박과 만료 건수를 먼저 확인해 우선 관리할 문서를 빠르게 찾습니다.",
  },
  '[data-autopilot="msds-list"]': {
    title: "사용 설비 확인",
    description:
      "목록의 사용 설비 컬럼에서 해당 MSDS가 어느 설비에 연결되어 있는지 확인합니다. 설비가 많으면 일부만 보이고 나머지는 +숫자로 표시됩니다.",
  },
  '[data-autopilot="msds-table-actions"]': {
    title: "검색과 수정",
    description:
      "제품명, 물질명, CAS No.로 검색하고 수정 아이콘에서 개정 정보, PDF, 사용 설비 연결을 갱신합니다.",
  },
};

const MSDS_AUTOPILOT_STEPS_WITH_MACHINE_MATCH = MSDS_AUTOPILOT_STEPS.map(
  (step) => ({
    ...step,
    ...(MSDS_AUTOPILOT_MACHINE_COPY[step.target] || {}),
  })
);

const resolvePdfUrl = (pdfUrl) => {
  if (!pdfUrl) return "";
  if (/^https?:\/\//i.test(pdfUrl)) return pdfUrl;
  return `${axiosInstance.defaults.baseURL || ""}${pdfUrl}`;
};

const getExpiryStatus = (expireAt) => {
  if (!expireAt) {
    return { label: "미설정", color: "default", daysLeft: null };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expireAt);
  expiry.setHours(0, 0, 0, 0);
  const daysLeft = Math.ceil((expiry.getTime() - today.getTime()) / 86400000);

  if (daysLeft < 0) return { label: "만료", color: "error", daysLeft };
  if (daysLeft <= 30) return { label: "만료 임박", color: "warning", daysLeft };
  return { label: "정상", color: "success", daysLeft };
};

const buildFormData = (form, file) => {
  const formData = new FormData();
  Object.entries(form).forEach(([key, value]) => {
    if (FORM_DATA_SKIP_KEYS.has(key)) return;
    if (value !== undefined && value !== null) {
      formData.append(
        key,
        Array.isArray(value) ? JSON.stringify(value) : value
      );
    }
  });
  if (file) {
    formData.append("pdf", file);
  }
  return formData;
};

const getMachineLabel = (machine) =>
  [
    machine?.safe_machine_name ||
      machine?.machine_name ||
      machine?.machine_manage_code ||
      `설비 ${machine?.machine_id}`,
    machine?.machine_manage_code,
  ]
    .filter(Boolean)
    .join(" · ");

function MsdsFormDialog({
  open,
  mode,
  form,
  file,
  onClose,
  onChange,
  onFileChange,
  onSubmit,
  isSaving,
  machineOptions,
  isMachineLoading,
}) {
  const isEdit = mode === "edit";
  const mergedMachineOptions = useMemo(() => {
    const map = new Map();
    [...(form.machines || []), ...(machineOptions || [])].forEach((machine) => {
      if (machine?.machine_id != null)
        map.set(Number(machine.machine_id), machine);
    });
    return Array.from(map.values());
  }, [form.machines, machineOptions]);
  const selectedMachines = useMemo(() => {
    const selectedIds = new Set(
      (form.machine_ids || []).map((id) => Number(id))
    );
    return mergedMachineOptions.filter((machine) =>
      selectedIds.has(Number(machine.machine_id))
    );
  }, [form.machine_ids, mergedMachineOptions]);

  const validateRequired = (key) =>
    REQUIRED_FIELDS.some((field) => field.key === key) &&
    !String(form[key] || "").trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {isEdit ? "MSDS 정보 수정" : "MSDS 신규 물질 등록"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              물질 정보와 원본 PDF를 등록하여 최신 MSDS 이력을 관리합니다.
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: "#fafafa" }}>
        <Grid container spacing={2.25}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="제품명"
              placeholder="ex) 산업용 세정제 A"
              value={form.product_name}
              error={validateRequired("product_name")}
              onChange={(event) => onChange("product_name", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="제조사"
              placeholder="ex) 대한화학(주)"
              value={form.manufacturer}
              onChange={(event) => onChange("manufacturer", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="주요 물질명"
              placeholder="ex) 톨루엔"
              value={form.substance_name}
              error={validateRequired("substance_name")}
              onChange={(event) =>
                onChange("substance_name", event.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="CAS No. / 이명"
              placeholder="ex) 108-88-3"
              value={form.cas_no}
              onChange={(event) => onChange("cas_no", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="함유량 (%)"
              placeholder="ex) 15~25%"
              value={form.content_ratio}
              onChange={(event) =>
                onChange("content_ratio", event.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="긴급 연락처"
              placeholder="ex) 02-1234-5678"
              value={form.emergency_contact}
              onChange={(event) =>
                onChange("emergency_contact", event.target.value)
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="유해·위험성 분류"
              placeholder="ex) 피부 과민성 물질 1구분, 흡입 유해성..."
              value={form.hazard_class}
              onChange={(event) => onChange("hazard_class", event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="위험물 안전관리법"
              placeholder="ex) 제4류 제1석유류 (비수용성)"
              value={form.dangerous_goods_act}
              onChange={(event) =>
                onChange("dangerous_goods_act", event.target.value)
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="주요 용도 및 월 사용량"
              placeholder="ex) 부품 세척용 / 월 50L"
              value={form.main_usage}
              onChange={(event) => onChange("main_usage", event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              loading={isMachineLoading}
              options={mergedMachineOptions}
              value={selectedMachines}
              getOptionLabel={getMachineLabel}
              isOptionEqualToValue={(option, value) =>
                Number(option.machine_id) === Number(value.machine_id)
              }
              onChange={(_, nextMachines) =>
                onChange(
                  "machine_ids",
                  nextMachines.map((machine) => Number(machine.machine_id))
                )
              }
              noOptionsText="등록된 설비가 없습니다."
              loadingText="설비 목록을 불러오는 중입니다."
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      {...tagProps}
                      size="small"
                      label={getMachineLabel(option)}
                      sx={{ borderRadius: "0.375rem", fontWeight: 700 }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="사용 설비"
                  placeholder="MSDS를 사용하는 설비 선택"
                  helperText="이 MSDS가 적용되는 설비를 여러 개 연결할 수 있습니다."
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="개정 버전"
              inputProps={{ min: 1 }}
              value={form.revision}
              onChange={(event) => onChange("revision", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="최근 개정일"
              value={form.revised_at || ""}
              onChange={(event) => onChange("revised_at", event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="만료일"
              value={form.expire_at || ""}
              onChange={(event) => onChange("expire_at", event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderStyle: "dashed",
                borderColor: file ? "primary.main" : COLORS.BORDER_DASH,
                backgroundColor: file ? "primary.lightOpacity" : "#fff",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <PictureAsPdfIcon color={file ? "primary" : "disabled"} />
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      MSDS 원본 파일 (PDF) {isEdit ? "" : "*"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {file
                        ? file.name
                        : "PDF 파일을 선택해 업로드하세요. 최대 10MB"}
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                >
                  파일 선택
                  <input
                    hidden
                    type="file"
                    accept="application/pdf"
                    onChange={(event) =>
                      onFileChange(event.target.files?.[0] || null)
                    }
                  />
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          취소
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? null : <ScienceIcon />}
        >
          {isEdit ? "수정 완료" : "등록 완료"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function StandardMsdsManagement() {
  const { group_code, project_code } = useParams();
  const api = useProjectApi(group_code, project_code);
  const queryClient = useQueryClient();
  const isReadOnly = useMediaQuery("(max-width:899.95px)");

  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [pdfFile, setPdfFile] = useState(null);
  const [autopilotOpen, setAutopilotOpen] = useState(false);
  const [autopilotStepIndex, setAutopilotStepIndex] = useState(0);

  const msdsQuery = useQuery({
    queryKey: ["risk-msds", group_code, project_code],
    queryFn: async () => {
      const response = await api.get("/msds");
      return response.data || [];
    },
    enabled: Boolean(group_code && project_code),
    placeholderData: [],
  });

  const machineQuery = useQuery({
    queryKey: ["risk-msds-machines", group_code, project_code],
    queryFn: async () => {
      const response = await api.get("/msds/machines");
      return response.data || [];
    },
    enabled: Boolean(group_code && project_code),
    placeholderData: [],
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildFormData(form, pdfFile);
      if (dialogMode === "edit") {
        return api.put(`/msds/${editingId}`, payload);
      }
      return api.post("/msds", payload);
    },
    onSuccess: () => {
      toast.success(
        dialogMode === "edit"
          ? "MSDS 정보가 저장되었습니다."
          : "MSDS가 등록되었습니다."
      );
      closeDialog();
      queryClient.invalidateQueries({
        queryKey: ["risk-msds", group_code, project_code],
      });
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "MSDS 저장 중 오류가 발생했습니다."
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (ids) => api.delete("/msds", { data: { ids } }),
    onSuccess: () => {
      toast.success("선택한 MSDS가 폐기 처리되었습니다.");
      setRowSelection({});
      queryClient.invalidateQueries({
        queryKey: ["risk-msds", group_code, project_code],
      });
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "MSDS 삭제 중 오류가 발생했습니다."
      );
    },
  });

  const openCreateDialog = () => {
    setDialogMode("create");
    setEditingId(null);
    setForm(EMPTY_FORM);
    setPdfFile(null);
    setDialogOpen(true);
  };

  const openAutopilot = () => {
    setAutopilotStepIndex(0);
    setAutopilotOpen(true);
  };

  const openEditDialog = (row) => {
    setDialogMode("edit");
    setEditingId(row.id);
    setForm({
      ...EMPTY_FORM,
      ...row,
      revision: row.revision || 1,
      revised_at: row.revised_at || "",
      expire_at: row.expire_at || "",
      machine_ids: row.machine_ids || [],
      machines: row.machines || [],
    });
    setPdfFile(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setPdfFile(null);
  };

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    const missing = REQUIRED_FIELDS.find(
      (field) => !String(form[field.key] || "").trim()
    );
    if (missing) {
      toast.warning(`${missing.label}을 입력해주세요.`);
      return;
    }
    if (dialogMode === "create" && !pdfFile) {
      toast.warning("MSDS 원본 PDF 파일을 업로드해주세요.");
      return;
    }
    saveMutation.mutate();
  };

  const selectedRows = useMemo(() => {
    const selectedIndexes = Object.keys(rowSelection).filter(
      (key) => rowSelection[key]
    );
    return selectedIndexes
      .map((rowId) => msdsQuery.data?.[Number(rowId)])
      .filter(Boolean);
  }, [rowSelection, msdsQuery.data]);

  const handleDelete = () => {
    const ids = selectedRows.map((row) => row.id);
    if (!ids.length) {
      toast.warning("폐기 처리할 MSDS를 선택해주세요.");
      return;
    }
    if (window.confirm(`${ids.length}건의 MSDS를 폐기 처리할까요?`)) {
      deleteMutation.mutate(ids);
    }
  };

  const summary = useMemo(() => {
    const rows = msdsQuery.data || [];
    return rows.reduce(
      (acc, row) => {
        const status = getExpiryStatus(row.expire_at);
        acc.total += 1;
        if (status.color === "warning") acc.expiring += 1;
        if (status.color === "error") acc.expired += 1;
        if (row.pdf_url) acc.withPdf += 1;
        return acc;
      },
      { total: 0, expiring: 0, expired: 0, withPdf: 0 }
    );
  }, [msdsQuery.data]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "product_name",
        header: "제품명",
        size: 190,
        Cell: ({ row }) => (
          <Stack spacing={0.4}>
            <Typography sx={{ fontWeight: 700 }}>
              {row.original.product_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.manufacturer || "제조사 미입력"}
            </Typography>
          </Stack>
        ),
      },
      {
        accessorKey: "substance_name",
        header: "주요 물질명",
        size: 160,
      },
      {
        accessorKey: "cas_no",
        header: "CAS No. / 이명",
        size: 140,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "content_ratio",
        header: "함유량",
        size: 110,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "hazard_class",
        header: "유해·위험성 분류",
        size: 240,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{
              maxWidth: 260,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={cell.getValue() || ""}
          >
            {cell.getValue() || "-"}
          </Typography>
        ),
      },
      {
        accessorKey: "machines",
        header: "사용 설비",
        size: 260,
        Cell: ({ row }) => {
          const machines = row.original.machines || [];
          if (!machines.length) {
            return <Typography color="text.secondary">-</Typography>;
          }
          return (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                columnGap: 1.2,
                rowGap: "0.5rem",
                width: "100%",
                maxWidth: 260,
                mx: "auto",
                alignItems: "center",
              }}
            >
              {machines.map((machine, index) => {
                const isLastOdd =
                  machines.length % 2 === 1 && index === machines.length - 1;
                return (
                  <Chip
                    key={machine.machine_id}
                    size="small"
                    variant="outlined"
                    label={getMachineLabel(machine)}
                    sx={{
                      borderRadius: "0.375rem",
                      minWidth: 0,
                      maxWidth: "100%",
                      justifySelf: "center",
                      gridColumn: isLastOdd ? "1 / -1" : "auto",
                      "& .MuiChip-label": {
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                );
              })}
            </Box>
          );
        },
      },
      {
        accessorKey: "revision",
        header: "개정",
        size: 80,
        Cell: ({ cell }) => (
          <Chip size="small" label={`v${cell.getValue() || 1}`} />
        ),
      },
      {
        accessorKey: "expire_at",
        header: "만료 상태",
        size: 130,
        Cell: ({ cell }) => {
          const status = getExpiryStatus(cell.getValue());
          return (
            <Tooltip
              title={
                status.daysLeft === null
                  ? "만료일이 설정되지 않았습니다."
                  : `${Math.abs(status.daysLeft)}일 ${
                      status.daysLeft < 0 ? "경과" : "남음"
                    }`
              }
            >
              <Chip size="small" color={status.color} label={status.label} />
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "pdf_url",
        header: "원본 PDF",
        size: 120,
        Cell: ({ cell }) => {
          const pdfUrl = cell.getValue();
          if (!pdfUrl) return <Typography color="text.secondary">-</Typography>;
          return (
            <Button
              size="small"
              href={resolvePdfUrl(pdfUrl)}
              target="_blank"
              rel="noreferrer"
              startIcon={<OpenInNewIcon />}
            >
              열기
            </Button>
          );
        },
      },
    ],
    []
  );

  return (
    <Box
      sx={{
        bgcolor: "var(--mui-palette-background-paper)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} />
      <Box sx={pageContainerSx}>
        <Stack spacing={1.5} sx={{ mb: 1.5 }}>
          <Box
            data-autopilot="msds-summary"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr)) auto",
              },
              gap: 1.5,
              alignItems: "stretch",
            }}
          >
            <Box>
              <MsdsSummaryCard
                icon={<ScienceIcon />}
                label="전체"
                value={`${summary.total}건`}
              />
            </Box>
            <Box>
              <MsdsSummaryCard
                icon={<PictureAsPdfIcon />}
                label="PDF 등록"
                value={`${summary.withPdf}건`}
                color="#16a34a"
              />
            </Box>
            <Box>
              <MsdsSummaryCard
                icon={<WarningAmberIcon />}
                label="만료 임박"
                value={`${summary.expiring}건`}
                color="#f59e0b"
              />
            </Box>
            <Box>
              <MsdsSummaryCard
                icon={<WarningAmberIcon />}
                label="만료"
                value={`${summary.expired}건`}
                color="#dc2626"
              />
            </Box>
            <Stack
              data-autopilot="msds-actions"
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              alignItems="center"
              sx={{
                minWidth: { md: 190 },
                alignSelf: "center",
                gridColumn: { xs: "1 / -1", md: "auto" },
              }}
            >
              <Tooltip title="오토파일럿">
                <IconButton
                  color="primary"
                  onClick={openAutopilot}
                  sx={iconActionButtonSx}
                >
                  <PlayCircleOutlineIcon />
                </IconButton>
              </Tooltip>
              {!isReadOnly ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreateDialog}
                >
                  MSDS 등록
                </Button>
              ) : null}
            </Stack>
          </Box>
        </Stack>

        <Paper
          data-autopilot="msds-list"
          elevation={0}
          sx={{
            ...standardPanelSx,
            overflow: "hidden",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MaterialReactTable
            columns={columns}
            data={msdsQuery.data || []}
            localization={MRT_Localization_KO}
            enableRowSelection={!isReadOnly}
            enableMultiRowSelection
            enableColumnActions={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableColumnFilters={false}
            enableStickyHeader
            state={{
              isLoading: msdsQuery.isLoading,
              showAlertBanner: msdsQuery.isError,
              showProgressBars: msdsQuery.isFetching,
              globalFilter,
              rowSelection,
            }}
            onGlobalFilterChange={setGlobalFilter}
            onRowSelectionChange={setRowSelection}
            muiToolbarAlertBannerProps={
              msdsQuery.isError
                ? {
                    color: "error",
                    children: "MSDS 데이터를 불러오는 중 오류가 발생했습니다.",
                  }
                : undefined
            }
            muiTablePaperProps={{
              sx: {
                boxShadow: "none",
                "& *": {
                  fontFamily: `${FONT_PROPS.PRIMARY}, sans-serif !important`,
                },
              },
            }}
            muiTableContainerProps={{
              sx: {
                maxHeight: "calc(100vh - 330px)",
              },
            }}
            muiTableHeadCellProps={{
              align: "center",
              sx: {
                fontWeight: 800,
                backgroundColor: "#f8fafc",
                textAlign: "center",
                "& .Mui-TableHeadCell-Content": {
                  justifyContent: "center",
                },
                "& .Mui-TableHeadCell-Content-Labels": {
                  justifyContent: "center",
                  width: "100%",
                },
                "& .Mui-TableHeadCell-Content-Wrapper": {
                  textAlign: "center",
                  width: "100%",
                },
              },
            }}
            renderTopToolbarCustomActions={() => (
              <Stack
                data-autopilot="msds-table-actions"
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
                sx={{ p: 2, width: "100%" }}
              >
                <TextField
                  size="small"
                  placeholder="제품명, 물질명, CAS No. 검색"
                  value={globalFilter || ""}
                  onChange={(event) => setGlobalFilter(event.target.value)}
                  sx={{ width: { xs: "100%", md: 320 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: globalFilter ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setGlobalFilter("")}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ display: { xs: "none", md: "block" } }}
                  />
                  {false ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      disabled={
                        !selectedRows.length || deleteMutation.isPending
                      }
                      onClick={handleDelete}
                      sx={{ fontWeight: 800, borderRadius: "0.375rem" }}
                    >
                      폐기
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            )}
            enableRowActions={!isReadOnly}
            positionActionsColumn="last"
            renderRowActions={({ row }) => (
              <Tooltip title="수정">
                <IconButton onClick={() => openEditDialog(row.original)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            renderEmptyRowsFallback={() => (
              <EmptyState
                compact
                icon={<ScienceIcon />}
                title="등록된 MSDS가 없습니다"
                description="MSDS 등록 버튼으로 물질 정보와 원본 PDF를 저장하면 만료 상태와 개정 이력을 함께 관리할 수 있습니다."
                actionLabel={isReadOnly ? undefined : "MSDS 등록"}
                actionIcon={isReadOnly ? undefined : <AddIcon />}
                onAction={isReadOnly ? undefined : openCreateDialog}
              />
            )}
          />
        </Paper>

        <Alert severity="info">
          원본 PDF와 만료일을 함께 등록하면 갱신이 필요한 문서를 빠르게 확인할
          수 있습니다.
        </Alert>

        <MsdsFormDialog
          open={dialogOpen}
          mode={dialogMode}
          form={form}
          file={pdfFile}
          onClose={closeDialog}
          onChange={handleFormChange}
          onFileChange={setPdfFile}
          onSubmit={handleSubmit}
          isSaving={saveMutation.isPending}
          machineOptions={machineQuery.data || []}
          isMachineLoading={machineQuery.isLoading || machineQuery.isFetching}
        />

        <AutoPilotGuide
          open={autopilotOpen}
          steps={MSDS_AUTOPILOT_STEPS_WITH_MACHINE_MATCH}
          stepIndex={autopilotStepIndex}
          onStepChange={setAutopilotStepIndex}
          onClose={() => setAutopilotOpen(false)}
        />
      </Box>
    </Box>
  );
}
